import { useEffect, useState } from 'react';

// A ticking elapsed-seconds counter for a long request.
//
// ⚠️⚠️ WHY THIS EXISTS: the AI copywriting paths run on Opus
// (MODELS.COPYWRITING), which takes tens of seconds — 15-25s for a post caption,
// up to a minute for a full campaign-copy run — plus reference fetching before
// the model is even called. A static "Working…" for that long reads as HUNG, and
// someone who thinks it has hung clicks again, which pays for a second Opus call.
// Showing the seconds tick and stating the expectation is the cheapest possible
// fix for both.
//
// ⚠️ IT LIVES IN ITS OWN FILE ON PURPOSE. The drafting surfaces are guarded by
// source-shape tests asserting that no `useEffect` there can reach a generation
// call, and the caption workspace additionally asserts it has NO effects at all.
// A timer is a legitimate effect that has nothing to do with generation; keeping
// it out here is what preserves those assertions at their strongest form.
//
// ⚠️ SHARED, not duplicated: the WAIT LABEL differs per feature (a caption run
// and a campaign-copy run take very different times, and an honest label has to
// say so), but the timer is the same everywhere and a second implementation
// would be a second thing to get wrong about background tabs.

export function useElapsedSeconds(active: boolean): number {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return;
    }
    const startedAt = Date.now();
    // Recompute from the clock rather than incrementing, so a throttled
    // background tab doesn't under-report the wait when the user comes back.
    const handle = setInterval(
      () => setSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => clearInterval(handle);
  }, [active]);

  return seconds;
}
